/**
 * Shared media domain types for the storage-upload pipeline.
 *
 * The backend exposes a single presigned-upload API (`/api/v1/storage/upload`)
 * that works for images, videos and PDFs across every feature (business gallery,
 * product media, user avatars). These types are the common contract so the
 * upload engine (useMediaUpload) and the feature services (BusinessApiService,
 * future ProductApiService) all speak the same language.
 */

// ─── Content types accepted by the storage API ───────────────────────────────
// Keep these in lockstep with the backend's allow-list.

export const ALLOWED_IMAGE_TYPES = [
  "image/jpeg",
  "image/png",
  "image/webp",
  "image/gif",
] as const;

export const ALLOWED_VIDEO_TYPES = [
  "video/mp4",
  "video/quicktime",
  "video/webm",
] as const;

export const ALLOWED_DOCUMENT_TYPES = ["application/pdf"] as const;

export type MediaContentType =
  | (typeof ALLOWED_IMAGE_TYPES)[number]
  | (typeof ALLOWED_VIDEO_TYPES)[number]
  | (typeof ALLOWED_DOCUMENT_TYPES)[number];

/** Flat list of every accepted content type — handy for validation. */
export const ALLOWED_MEDIA_TYPES: readonly MediaContentType[] = [
  ...ALLOWED_IMAGE_TYPES,
  ...ALLOWED_VIDEO_TYPES,
  ...ALLOWED_DOCUMENT_TYPES,
];

/** Storage bucket folder. Allowed values are fixed by the backend. */
export type MediaFolder = "products" | "users" | "business";

/** Coarse UI category derived from the content type — drives how a tile renders. */
export type MediaKind = "image" | "video" | "pdf";

// ─── Persisted media record ───────────────────────────────────────────────────

/**
 * A single media item belonging to a business gallery (or, later, a product).
 *  - `objectPath` is what the DB stores and what DELETE targets.
 *  - `publicUrl` is the permanent fetch URL (never expires).
 *  - `section` is the optional gallery section label (Phase 3); undefined =
 *    ungrouped. Backend persists/returns it on the same media record.
 */
export interface MediaRecord {
  mediaType: MediaContentType;
  objectPath: string;
  publicUrl: string;
  section?: string;
  /**
   * Small pre-generated thumbnail, when the backend has one stored for this item
   * (business gallery images only, for now — see BusinessImageService#tryGenerateThumbnail).
   * Absent for records the backend hasn't generated a thumbnail for (product images,
   * videos, legacy items not yet backfilled) — callers should fall back to publicUrl,
   * never construct a transform URL client-side.
   */
  thumbnailUrl?: string;
}

/**
 * Thumbnail box (px) requested for gallery image thumbnails. The backend clamps each
 * dimension independently to a sane range rather than trusting it outright, but does not
 * otherwise decide it -- this is the single source of truth for gallery thumbnail size on
 * the UI side.
 *
 * 400x400 matches what this UI used to request directly from Supabase Storage's on-the-fly
 * Image Transformation endpoint (`width=400&height=400&resize=cover&quality=70`) before
 * thumbnails were generated and stored server-side -- the backend cover-crops to fill this
 * box exactly, so thumbnails look the same as they did under that old scheme.
 */
export const GALLERY_THUMBNAIL_WIDTH = 400;
export const GALLERY_THUMBNAIL_HEIGHT = 400;

/** What the persist endpoint expects per item (`PUT .../update`). */
export interface MediaPersistItem {
  mediaType: MediaContentType;
  objectPath: string;
  section?: string;
  /** See GALLERY_THUMBNAIL_WIDTH/GALLERY_THUMBNAIL_HEIGHT. Ignored by the backend for non-image media. */
  thumbnailWidth?: number;
  thumbnailHeight?: number;
}

// ─── Upload result contract ───────────────────────────────────────────────────
// Each file flows through a 3-step pipeline (presign → put → persist). Failures
// are isolated per file and carry the stage they failed at, so the UI can report
// precisely and offer per-file retry without re-uploading the ones that worked.

export type UploadStage = "validate" | "presign" | "put" | "persist";

export interface MediaUploadSuccess {
  status: "success";
  file: File;
  record: MediaRecord;
}

export interface MediaUploadFailure {
  status: "error";
  file: File;
  stage: UploadStage;
  message: string;
}

export type MediaUploadResult = MediaUploadSuccess | MediaUploadFailure;

/** Aggregate outcome of an upload batch. */
export interface MediaUploadBatch {
  results: MediaUploadResult[];
  /** Records that uploaded AND persisted successfully. */
  succeeded: MediaRecord[];
  /** Files that failed at any stage. */
  failed: MediaUploadFailure[];
}

// ─── Optimistic upload UI state ───────────────────────────────────────────────
// Surfaced by the gallery hook so the grid can show "uploading" placeholder tiles
// (with a local preview) while a file is in flight, and an error/retry state if
// it fails — rather than only a button spinner.

export interface PendingUpload {
  id: string;
  file: File;
  kind: MediaKind;
  /** Object URL for an instant local preview (images/videos only). */
  previewUrl?: string;
  /** Target section, when sections are enabled. */
  section?: string;
  status: "uploading" | "error";
  /** Failure message when status is "error". */
  message?: string;
}
